// Rich "View Results" / "View Imaging Result" / "View Dispensing"
// modal for the doctor.
//
// One component drives all three surfaces with kind-aware copy. The
// modal is structured around what the doctor needs at a glance:
//
//   1. Status badge — Pending / Preliminary / Final (or pharmacy
//      equivalents). Colour-coded so the lifecycle reads instantly.
//   2. What was ordered — list of test/study/drug names so the doctor
//      doesn't have to context-switch back to the order card.
//   3. Preliminary summary — short clinical text the lab/radiology/
//      pharmacy entered as soon as they had something interpretable
//      (this is the key surface for empirical-treatment decisions).
//   4. Impression (imaging only) — the radiologist's bottom line.
//   5. Provider comment — free-text flags (specimen quality, repeat
//      test, counselling delivered, etc.).
//   6. Uploaded documents — file list with Print / Email / Save. The
//      modal does NOT let the doctor edit anything; it's read-only.
//
// Design constraint from the user: View Results = quick clinical read,
// Print/Email/Save = the uploaded document (separate concern). Both
// surfaces live in the same modal but stay visually distinct so the
// doctor doesn't conflate the two.

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { emailDocument, printDocument, downloadDocument } from '../../../api/documents.js'
import Modal, { Field, ModalActions, PrimaryButton, SecondaryButton } from './Modal.jsx'
import EmailDocumentModal from './EmailDocumentModal.jsx'

const STATUS_TONE = {
  pending:     { bg: 'bg-amber-100',   text: 'text-amber-800',   ring: 'ring-amber-300',   label: 'Pending' },
  preliminary: { bg: 'bg-sky-100',     text: 'text-sky-800',     ring: 'ring-sky-300',     label: 'Preliminary' },
  final:       { bg: 'bg-emerald-100', text: 'text-emerald-800', ring: 'ring-emerald-300', label: 'Final' },
  // pharmacy
  dispensed:           { bg: 'bg-emerald-100', text: 'text-emerald-800', ring: 'ring-emerald-300', label: 'Dispensed' },
  partially_dispensed: { bg: 'bg-amber-100',   text: 'text-amber-800',   ring: 'ring-amber-300',   label: 'Partially dispensed' },
  not_available:       { bg: 'bg-rose-100',    text: 'text-rose-800',    ring: 'ring-rose-300',    label: 'Not available' },
  substituted:         { bg: 'bg-violet-100',  text: 'text-violet-800',  ring: 'ring-violet-300',  label: 'Substituted' },
}

function StatusBadge({ status }) {
  const tone = STATUS_TONE[(status || '').toLowerCase()] || STATUS_TONE.pending
  return (
    <span className={`inline-flex items-center gap-1 ${tone.bg} ${tone.text} ring-1 ring-inset ${tone.ring} rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider`}>
      {tone.label}
    </span>
  )
}

/**
 * Top-level component. Props:
 *   kind         'lab' | 'imaging' | 'rx'
 *   title        modal title (e.g. "Lab order #10 — Results")
 *   subtitle     modal subtitle (e.g. "Issued by ICER Diagnostic Laboratory")
 *   ordered      [{label}]  — what was ordered (tests / studies / drugs)
 *   status       lifecycle status (pending/preliminary/final OR dispensed/etc.)
 *   prelim       preliminary_result_summary / preliminary_imaging_summary text
 *   final        result_summary / report_summary text (formal / final)
 *   impression   imaging only — radiologist's impression
 *   comment      result_comment / pharmacist_comment
 *   files        [{id, original_filename, file_url, kind, size_bytes, uploaded_at, uploaded_by_username}]
 *   items        lab only — full LabOrderItem[] inc. nested analytes[] and is_panel.
 *                Drives the read-only analyte/test results table for the doctor.
 *   encounter    encounter object (used by the email modal)
 *   onClose
 *   onGenerate   optional — opens the branded-PDF generator
 */
export default function PreliminaryResultsModal({
  kind, title, subtitle,
  ordered = [], status, prelim, final, impression, comment,
  files = [], items = [], encounter,
  onClose, onGenerate,
}) {
  const [emailDoc, setEmailDoc] = useState(null)

  const labels = {
    lab:     { ordered: 'Tests requested',   prelimLabel: 'Preliminary result summary',
               finalLabel: 'Final result summary',  commentLabel: 'Lab comments',
               filesLabel: 'Uploaded result documents', source: 'lab' },
    imaging: { ordered: 'Studies requested', prelimLabel: 'Preliminary imaging summary',
               finalLabel: 'Final report summary',   commentLabel: 'Radiologist comments',
               filesLabel: 'Uploaded reports',  source: 'imaging centre' },
    rx:      { ordered: 'Medications',       prelimLabel: 'Dispensing notes',
               finalLabel: '',                       commentLabel: 'Pharmacist comments',
               filesLabel: 'Uploaded documents', source: 'pharmacy' },
  }
  const L = labels[kind] || labels.lab

  return (
    <>
      <Modal title={title} subtitle={subtitle} onClose={onClose} wide>
        {/* Header strip — status + ordered list at the top so the
            doctor's first glance answers "what was ordered + how far
            along is it?" */}
        <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b-2 border-slate-100">
          <StatusBadge status={status} />
          {ordered.length > 0 && (
            <div className="text-xs text-slate-500 truncate text-right">
              <span className="uppercase tracking-wider font-bold mr-1.5">{L.ordered}</span>
              {ordered.map((o) => o.label).join(' · ')}
            </div>
          )}
        </div>

        {/* Structured analyte / test results — lab kind only.
            Mirrors the lab tech's report-style table but read-only:
            the doctor sees Hb, WBC, Cr, etc. as discrete numeric rows
            (with abnormal flags tinted), not buried inside a prose
            summary. Panel orders (FBC, UEC, lipid, HbA1c) expand into
            their analyte sub-rows; single-tests (Malaria RDT, Dengue NS1)
            render as one flat row using the parent item's value.
            Hidden when no item carries any result yet — the prose
            preliminary card below is enough on its own. */}
        {kind === 'lab' && hasAnyResult(items) && (
          <LabAnalyteResultsTable items={items} />
        )}

        {/* Preliminary summary — the doctor's empirical-treatment cue */}
        {prelim ? (
          <div className="bg-sky-50 border-2 border-sky-200 rounded-lg p-4 mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-sky-800 mb-1.5">
              {L.prelimLabel}
            </div>
            <pre className="whitespace-pre-wrap text-base text-slate-900 font-sans leading-relaxed">{prelim}</pre>
          </div>
        ) : null}

        {/* Impression — imaging only, given more weight than a generic
            comment because it's the radiologist's bottom line. */}
        {kind === 'imaging' && impression && (
          <div className="bg-violet-50 border-2 border-violet-200 rounded-lg p-4 mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-violet-800 mb-1.5">
              Impression
            </div>
            <pre className="whitespace-pre-wrap text-base text-slate-900 font-sans leading-relaxed">{impression}</pre>
          </div>
        )}

        {/* Final summary (separate card, distinct tone) — only shown if
            different from the preliminary, otherwise it duplicates. */}
        {final && final.trim() !== (prelim || '').trim() && L.finalLabel && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4 mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-1.5">
              {L.finalLabel}
            </div>
            <pre className="whitespace-pre-wrap text-base text-slate-900 font-sans leading-relaxed">{final}</pre>
          </div>
        )}

        {/* Provider comment */}
        {comment && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              {L.commentLabel}
            </div>
            <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed">{comment}</pre>
          </div>
        )}

        {/* Empty state — only when truly nothing yet */}
        {!prelim && !final && !impression && !comment && files.length === 0 &&
         !(kind === 'lab' && hasAnyResult(items)) && (
          <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-4 mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Nothing recorded yet
            </div>
            <p className="text-sm text-slate-700">
              The {L.source} has not entered a summary or uploaded anything for this order.
              Please check back, or contact the {L.source} if you're awaiting results.
            </p>
          </div>
        )}

        {/* Uploaded documents — read-only, with Print / Email / Save
            per file. This is the formal-document surface; the modal
            content above is the quick-read surface. */}
        {files.length > 0 && (
          <div className="border-t-2 border-slate-100 pt-3 mt-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-2">
              {L.filesLabel} ({files.length})
            </div>
            <ul className="space-y-1.5">
              {files.map((f) => (
                <FileRow key={f.id} file={f} onEmail={setEmailDoc} />
              ))}
            </ul>
          </div>
        )}

        {/* Empirical-treatment guidance banner — only when there's a
            preliminary summary (otherwise misleading). */}
        {prelim && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 mt-3">
            <strong className="font-bold">For empirical treatment guidance.</strong>{' '}
            The formal document (uploaded by the {L.source}) is the authoritative record.
          </div>
        )}

        <ModalActions>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          {onGenerate && (prelim || final || impression || comment) && (
            <PrimaryButton onClick={onGenerate}>Generate full PDF</PrimaryButton>
          )}
        </ModalActions>
      </Modal>

      {emailDoc && (
        <EmailDocumentModal
          doc={emailDoc}
          encounter={encounter}
          onClose={() => setEmailDoc(null)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------

function FileRow({ file, onEmail }) {
  const hasFile = Boolean(file?.id)
  const handlePrint = () => { printDocument(file).catch(() => {}) }
  const handleSave = () => { downloadDocument(file).catch(() => {}) }
  return (
    <li className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-3">
      <span className="text-xl shrink-0">📄</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-900 truncate">
          {file.original_filename || `Document #${file.id}`}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {(file.kind || 'document').replace(/_/g, ' ')}
          {file.size_bytes != null && <> · {formatBytes(file.size_bytes)}</>}
          {file.uploaded_by_username && <> · by {file.uploaded_by_username}</>}
        </div>
      </div>
      {hasFile && (
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={handlePrint} className="text-xs font-bold text-wantok-700 hover:bg-wantok-50 border border-wantok-700 rounded px-2 py-1">🖨️ Print</button>
          <button onClick={() => onEmail?.(file)} className="text-xs font-bold text-wantok-700 hover:bg-wantok-50 border border-wantok-700 rounded px-2 py-1">✉️ Email</button>
          <button onClick={handleSave} className="text-xs font-bold text-wantok-700 hover:bg-wantok-50 border border-wantok-700 rounded px-2 py-1">💾 Save</button>
        </div>
      )}
    </li>
  )
}

function formatBytes(n) {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// ---------------------------------------------------------------------------
// Read-only structured-results table (lab kind).
//
// The lab tech enters values per-analyte (for panels: FBC → Hb, WBC, Plt…)
// or per-test (singles: Malaria RDT, Dengue NS1). The doctor reads them
// here in the same shape they'd see on a paper report. Read-only — no
// inputs, no PATCHes — the only writeable surface is the lab tech's
// LabOrderDetail page. Abnormal flags are colour-pilled and the
// containing row is tinted so a doctor scanning the table catches an
// out-of-range value at a glance.
// ---------------------------------------------------------------------------

/** Does any item / analyte on this order carry a non-empty result_value? */
function hasAnyResult(items) {
  if (!Array.isArray(items) || items.length === 0) return false
  return items.some((it) => {
    if ((it.result_value || '').trim() !== '') return true
    return (it.analytes || []).some((a) => (a.result_value || '').trim() !== '')
  })
}

const FLAG_TONE = {
  '':   'bg-slate-50 text-slate-500 border-slate-200',
  L:    'bg-amber-50 text-amber-800 border-amber-300',
  H:    'bg-amber-50 text-amber-800 border-amber-300',
  LL:   'bg-rose-100 text-rose-800 border-rose-300',
  HH:   'bg-rose-100 text-rose-800 border-rose-300',
  A:    'bg-rose-100 text-rose-800 border-rose-300',
}

function rowTintForFlag(flag) {
  // Critical (LL/HH/A) gets a stronger rose tint; mild (L/H) gets amber.
  // No tint when in-range — keeps the table calm on normal values.
  if (['LL', 'HH', 'A'].includes(flag)) return 'bg-rose-50/70'
  if (['L', 'H'].includes(flag))        return 'bg-amber-50/50'
  return ''
}

function FlagPill({ flag }) {
  const f = (flag || '').toUpperCase()
  if (!f) return <span className="text-slate-300 text-[11px]">—</span>
  const tone = FLAG_TONE[f] || FLAG_TONE['']
  return (
    <span className={`inline-flex items-center justify-center min-w-[1.5rem] border rounded px-1.5 py-0.5 text-[11px] font-bold ${tone}`}>
      {f}
    </span>
  )
}

function LabAnalyteResultsTable({ items = [] }) {
  // Skip items that have no content of their own AND no analyte content —
  // keeps the table from showing blank panel headers when nothing is in.
  const visible = items.filter((it) => {
    const ownValue = (it.result_value || '').trim()
    const anyAnalyte = (it.analytes || []).some((a) => (a.result_value || '').trim() !== '')
    return ownValue !== '' || anyAnalyte || it.is_panel
  })
  if (visible.length === 0) return null

  return (
    <div className="border-2 border-slate-200 rounded-lg overflow-hidden mb-3">
      <div className="bg-slate-50 border-b-2 border-slate-200 px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
          Structured results
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left font-semibold px-3 py-2">Test / Analyte</th>
              <th className="text-left font-semibold px-3 py-2 w-32">Value</th>
              <th className="text-left font-semibold px-3 py-2 w-24">Unit</th>
              <th className="text-left font-semibold px-3 py-2 w-32">Ref. range</th>
              <th className="text-left font-semibold px-3 py-2 w-20">Flag</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((it) => (
              it.is_panel
                ? <PanelRowsReadOnly key={it.id || it.test_code} item={it} />
                : <SingleRowReadOnly key={it.id || it.test_code} item={it} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Single (non-panel) test — one row, value lives on the parent item. */
function SingleRowReadOnly({ item }) {
  const flag = (item.abnormal_flag || '').toUpperCase()
  const value = item.result_value || ''
  const tint = rowTintForFlag(flag)
  return (
    <tr className={`border-b border-slate-100 align-top ${tint}`}>
      <td className="px-3 py-2">
        <div className="text-sm font-medium text-slate-800">{item.test_name}</div>
        {item.test_code && (
          <div className="text-[10px] font-mono text-slate-400">{item.test_code}</div>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-slate-900">
        {value || <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-2 text-sm text-slate-700">{item.result_unit || ''}</td>
      <td className="px-3 py-2 text-sm text-slate-700">{item.reference_range || ''}</td>
      <td className="px-3 py-2"><FlagPill flag={flag} /></td>
    </tr>
  )
}

/** Panel test — header row + N analyte sub-rows (always expanded; the
 *  doctor's modal isn't optimised for hiding values, and the panel size
 *  is bounded by the catalogue template). */
function PanelRowsReadOnly({ item }) {
  const analytes = item.analytes || []
  const abnormalCount = analytes.filter(
    (a) => ['L', 'H', 'LL', 'HH', 'A'].includes((a.abnormal_flag || '').toUpperCase())
  ).length
  return (
    <>
      {/* Panel header row — visually distinct from the analyte rows */}
      <tr className="bg-slate-50 border-b border-slate-200">
        <td colSpan={5} className="px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-800">{item.test_name}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              · panel · {analytes.length} analyte{analytes.length === 1 ? '' : 's'}
            </span>
            {item.test_code && (
              <span className="text-[10px] font-mono text-slate-400">{item.test_code}</span>
            )}
            {abnormalCount > 0 && (
              <span className="ml-auto text-[10px] uppercase font-bold text-rose-700 bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5">
                {abnormalCount} abnormal
              </span>
            )}
          </div>
        </td>
      </tr>
      {analytes.length === 0 && (
        <tr><td colSpan={5} className="px-3 py-2 text-[12px] text-slate-400 italic">
          No analytes have been instantiated for this panel.
        </td></tr>
      )}
      {analytes.map((a) => {
        const flag = (a.abnormal_flag || '').toUpperCase()
        const tint = rowTintForFlag(flag)
        return (
          <tr key={a.id} className={`border-b border-slate-100 align-top ${tint}`}>
            <td className="px-3 py-1.5 pl-8">
              <div className="text-sm text-slate-800">{a.analyte_name}</div>
              <div className="text-[10px] text-slate-400 flex flex-wrap gap-x-2">
                {a.short_code && <span className="font-mono">{a.short_code}</span>}
                {a.loinc_code && <span>LOINC {a.loinc_code}</span>}
              </div>
            </td>
            <td className="px-3 py-1.5 text-sm text-slate-900">
              {a.result_value || <span className="text-slate-300">—</span>}
            </td>
            <td className="px-3 py-1.5 text-sm text-slate-700">{a.result_unit || ''}</td>
            <td className="px-3 py-1.5 text-sm text-slate-700">{a.reference_range || ''}</td>
            <td className="px-3 py-1.5"><FlagPill flag={flag} /></td>
          </tr>
        )
      })}
    </>
  )
}
